import React, { useEffect, useRef, useState } from 'react'
import { MARKET_TYPE_MAPPING } from '../config/markets'
import { playerImageUrl, teamLogoUrl, isIndividualSport } from '../config/playerImages'

// ============================================================================
// Live bet meter — the expanded row under a pending bet whose game is in play.
//
// DISPLAY ONLY. Everything here reads what liveProgress() already computed and
// draws it. Nothing in this file may influence a bet's status; settlement is
// settleBet / settleMulti and this component is never consulted by either.
//
// Two shapes, chosen by `live.meter` (see marketKind in settlement.js):
//   progress — accumulating value climbing toward a line (props, totals)
//   margin   — value swinging both ways around a centre (h2h, handicap)
//
// Colour convention: the game context on the left is deliberately monochrome so
// the meter on the right is the only thing carrying a verdict. Two competing
// colour systems in one row reads as noise.
//
// LAYOUT lives here; the PARTS are shared. LiveBetMeterMobile.jsx imports the
// meters, the pips, winningNow, the period bar and the images from this file and
// lays them out as a card instead of a table row. Only the arrangement differs,
// which is the point: a pip on a phone can no more contradict its bar than one
// on a desktop, because both read the same winningNow().
// ============================================================================

export const MUTED = '#64748b'

// One tone per state. Only `win` carries a ring/halo: a winning bet is the one
// thing on the row worth lighting up, and glowing every state would flatten the
// signal back to nothing.
// `bg` is deliberately OPAQUE. The travelling chip sits on top of the bar it is
// measuring, so any transparency lets the fill show straight through the number.
export const TONE = {
  win:  { main: '#34d399', bg: '#0d2a26', ring: 'rgba(52,211,153,0.55)', halo: 'rgba(52,211,153,0.22)' },
  live: { main: '#fbbf24', bg: '#2b2211', ring: null, halo: null },
  lost: { main: '#f87171', bg: '#2b1618', ring: null, halo: null },
  none: { main: '#7c8ba1', bg: '#1a2233', ring: null, halo: null }
}

const TRACK_BG = '#16233c'
const TRACK_H  = '5px'

// Where every meter starts, measured from the panel's content edge. A single and
// a multi leg reserve exactly this much for whatever sits to the left of the bar
// — the same avatar and selection text on both — so the tracks come out the same
// length and line up whichever kind of bet is open.
//
// Fixed rather than content-width on purpose: it is what stops a long selection
// from shortening its own bar, and what keeps a multi's legs reading as one
// column down the panel.
const METER_INSET = '313px'

// Breathing room between that block and the bar. Sits INSIDE the reserve, since
// the whole point is that nothing moves where the bar starts.
const METER_GUTTER = '10px'

// The image-and-selection block, drawn as an outline.
//
// No border and no fill. The outline that used to group the face with the words
// next to it was drawn back when the surfaces around it were lighter; against the
// current near-black panel the proximity does that job on its own, and the box had
// started reading as a card within a card.
//
// The padding stays: it is what holds the group off the meter to its right, and
// the reserve it sits inside is a fixed width, so the bar starts where it always did.
const SUBJECT_BOX = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: 0,
  padding: '6px 9px',
  borderRadius: '9px'
}

/** A value chip. `lit` gives it the tone's colour, and its glow if it has one. */
const chip = (tone, lit) => ({
  ...bubbleStyle,
  color: lit ? tone.main : '#cbd5e1',
  borderColor: lit ? tone.main : '#35486b',
  backgroundColor: lit ? tone.bg : '#16223a',
  boxShadow: lit && tone.ring ? `0 0 7px ${tone.ring}, 0 0 16px ${tone.halo}` : 'none',
  textShadow: lit && tone.ring ? `0 0 9px ${tone.ring}` : 'none'
})

/** The filled portion of a track, glowing only when the bet is winning. */
const fillStyle = (tone) => ({
  height: '100%',
  borderRadius: '3px',
  backgroundColor: tone.main,
  boxShadow: tone.ring ? `0 0 6px ${tone.ring}, 0 0 14px ${tone.halo}` : 'none'
})

/**
 * Team name for the travelling chip, which has to fit between the two axis
 * labels on a track that a multi leg makes narrow. Takes the last word, which is
 * the nickname in every sport that names teams this way: Greater Western Sydney
 * Giants -> Giants, Port Adelaide Power -> Power, Los Angeles Lakers -> Lakers.
 * Single-word names are returned untouched.
 *
 * Safe because it is never the only label: both FULL names are printed at the
 * ends of the same axis, directly under the chip, so the short form always has
 * its long form on screen beside it. Deliberately not a lookup table — one would
 * need an entry per team per league and would silently fall back to the full
 * name for anything it missed, which is exactly the case that overflows.
 */
const shortTeam = (name) => {
  const parts = String(name ?? '').trim().split(/\s+/)
  return parts.length > 1 ? parts[parts.length - 1] : (parts[0] || '')
}

/** Trims float noise: 3.5 stays 3.5, 27.0 becomes 27. */
export const fmt = (n) => {
  if (n == null) return ''
  const r = Math.round(n * 100) / 100
  return String(r)
}

const bubbleStyle = {
  padding: '2.5px 8px',
  borderRadius: '6px',
  border: '1px solid #35486b',
  backgroundColor: '#16223a',
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: 1.35,
  letterSpacing: '0.01em',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap'
}

// Animation classes (bc-lm-*) live in src/styles/app.css.

/** Fires true for `ms` each time `value` changes to a new non-null number. */
export function useChangePulse(value, ms) {
  const prev = useRef(value)
  const [on, setOn] = useState(false)
  useEffect(() => {
    const was = prev.current
    prev.current = value
    if (was == null || value == null || was === value) return
    setOn(true)
    const t = setTimeout(() => setOn(false), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return on
}

/** Fires true for `ms` on a false -> true transition only. */
function useRisingPulse(value, ms, enabled = true) {
  const prev = useRef(value)
  const [on, setOn] = useState(false)
  useEffect(() => {
    const was = prev.current
    prev.current = value
    if (!enabled || was !== false || value !== true) return
    setOn(true)
    const t = setTimeout(() => setOn(false), ms)
    return () => clearTimeout(t)
  }, [value, ms, enabled])
  return on
}

// ── Period bar ──────────────────────────────────────────────────────────────

// `marginTop` is a prop because this sits under a heading in most places, but
// rides inline in a centred row on the collapsed multi strip, where the offset
// would push it off the row's centre line.
export function PeriodBar({ segments, period, completedPeriods, periodFraction, marginTop = '7px' }) {
  if (!segments) return null
  return (
    <div style={{ display: 'flex', gap: '3px', marginTop }}>
      {Array.from({ length: segments }, (_, i) => {
        const n = i + 1
        const fraction = n <= completedPeriods ? 1 : (n === period ? periodFraction : 0)
        return (
          <div key={n} style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: '#1e2c47', overflow: 'hidden' }}>
            <div
              className="bc-lm-seg"
              style={{
                width: `${fraction * 100}%`,
                height: '100%',
                borderRadius: '2px',
                backgroundColor: fraction === 1 ? '#6b7f9e' : '#9fb2cc'
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

// ── Meters ──────────────────────────────────────────────────────────────────

/**
 * Accumulating value against a line. The target sits at the end of the track,
 * so the bar simply fills toward it and a value past its line pins at full.
 * Overshoot is carried by the colour and the readout rather than by geometry.
 */
function progressGeometry(current, line) {
  if (current == null || line == null || line === 0) return null
  return Math.max(0, Math.min(100, (current / line) * 100))
}

/**
 * Margin around a centre. The centre is the line (zero for head_to_head, the
 * handicap for a line bet) and the fill runs out from it, right for covering.
 * The scale grows with the margin so a blowout still fits.
 */
function marginGeometry(current, line) {
  if (current == null) return null
  const adjusted = current + (line ?? 0)
  // Wide enough for the running margin AND for the draw marker, which sits at
  // the handicap's own value. Without the second term a big line would peg its
  // draw marker to the end of the track.
  const range = Math.max(40, Math.abs(adjusted) * 1.3, Math.abs(line ?? 0) * 1.15)
  const at = (value) => Math.max(-50, Math.min(50, (value / range) * 50))
  return { adjusted, offset: at(adjusted), at }
}

/**
 * Would this selection win if its game ended right now, or null when that is
 * not knowable. Position, not pace: an Under sitting below its line early reads
 * as winning even when the run rate says otherwise. True as stated, and it never
 * claims a verdict — settleBet is the only thing that decides anything.
 */
export function winningNow(live) {
  if (!live) return null
  const { meter, current, line, side } = live

  if (meter === 'progress') {
    if (current == null || line == null) return null
    const lower = String(side ?? '').toLowerCase()
    if (lower === 'over') return current > line
    if (lower === 'under') return current < line
    return null
  }

  if (meter === 'margin') {
    if (current == null) return null
    return (current + (line ?? 0)) > 0
  }

  return null
}

/**
 * The tone a live state renders in. Exported so the phone's text readout picks
 * its colour the same way the bar does: `winningNow` first, and lost only once
 * the bet's own window has CLOSED. A bet behind with a quarter to play is amber,
 * not red, whichever platform is drawing it.
 */
export function toneFor(live) {
  const winning = winningNow(live)
  if (winning == null) return TONE.none
  return winning ? TONE.win : (live?.decided ? TONE.lost : TONE.live)
}

/**
 * Who is in front on a margin market, in plain terms.
 *
 * A signed margin is unreadable on its own: -31 on a North Melbourne bet does
 * not say that St Kilda are the ones 31 in front. `text` takes shortTeam() for
 * anywhere space is tight and `full` keeps the whole name for a title.
 */
export function marginLead(live) {
  const { current, teams, backedTeam } = live
  const opponent = teams
    ? (teams.home.team === backedTeam ? teams.away.team : teams.home.team)
    : null

  const leader = current === 0 ? null : (current > 0 ? backedTeam : opponent)
  const by = fmt(Math.abs(current ?? 0))

  return {
    opponent,
    leader,
    text: leader ? `${shortTeam(leader)} by ${by}` : 'Scores level',
    full: leader ? `${leader} by ${by}` : 'Scores level'
  }
}

/**
 * `compact` is the phone, and all it does is tighten the vertical padding: a
 * card has no table row height to sit inside.
 *
 * Only the PROGRESS shape is ever drawn compact. A value climbing toward a
 * target pinned at the end of the track survives a 300px card intact. The margin
 * shape does not — see MarginReadout in LiveBetMeterMobile.jsx, which keeps its
 * reading and drops its bar.
 */
export function Meter({ live, compact = false }) {
  // `decided` not `final`: a first-half bet is settled business at the break,
  // with two quarters still to play, so that is when it stops being live.
  const { meter, current, line, side, label, backedTeam } = live
  const final = live.decided

  // Shared with the multi summary, so a leg's colour and the count of legs
  // still alive can never disagree.
  const winning = winningNow(live)
  const geometry = meter === 'progress'
    ? progressGeometry(current, line)
    : meter === 'margin'
      ? marginGeometry(current, line)
      : null

  const flash = useChangePulse(current, 400)
  const bloom = useRisingPulse(winning, 900, !final)

  if (!meter || geometry == null) {
    // A market with no meaningful running value (tryscorer, correct score) or
    // one missing its line. The scoreline on the left still tells the story.
    return label
      ? <div style={{ fontSize: '12.5px', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', paddingTop: compact ? '9px' : 0 }}>{label}</div>
      : null
  }

  const tone = toneFor(live)

  // ── Accumulating value: bar fills toward a target pinned at the end ────────
  if (meter === 'progress') {
    // Once the bar is full the travelling bubble has arrived at the target, so
    // the two would sit on top of each other. They merge instead: the end
    // bubble stops showing the target and takes over the live value.
    const reached = geometry >= 100
    return (
      <div style={{ paddingTop: compact ? '13px' : '15px', paddingBottom: compact ? '3px' : '9px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          {/* The track excludes the end bubble, so the travelling bubble can run
              the full length without ever colliding with it. */}
          <div style={{ flex: 1, position: 'relative', height: TRACK_H, borderRadius: '3px', backgroundColor: TRACK_BG }}>
            <div
              className={`bc-lm-fill${bloom ? ' bc-lm-bloom' : ''}`}
              style={{ ...fillStyle(tone), width: `${geometry}%` }}
            />
            {!reached && (
              // Rides the leading edge, sitting on the track like a handle. Same
              // sliding anchor the margin meter uses, so neither chip has to stop
              // short of the tip to stay on the track.
              <div
                className={`bc-lm-fill${flash ? ' bc-lm-flash' : ''}`}
                style={{
                  ...chip(tone, true),
                  position: 'absolute',
                  top: '50%',
                  left: `${geometry}%`,
                  transform: `translate(-${geometry}%, -50%)`,
                  zIndex: 2
                }}
              >
                {fmt(current)}
              </div>
            )}
          </div>

          <div
            className={reached && flash ? 'bc-lm-flash' : undefined}
            style={chip(tone, reached)}
          >
            {fmt(reached ? current : line)}
          </div>
        </div>
      </div>
    )
  }

  // ── Margin: fills out from a centre, with the axis labelled at each end ────
  // Without the team names the direction is a guess, since the bar running left
  // could equally mean the backed side is behind or that it is ahead.
  const { opponent, leader, text: leadText, full: leadFull } = marginLead(live)

  // A handicap carries a point; head_to_head does not. That is the only thing
  // separating the two, and it decides what the centre of the track means.
  const hasLine = line != null

  // Level scores sit at the handicap's own value, not at the centre: give a side
  // +25.5 and a drawn game leaves them 25.5 to the good. Hide the label when it
  // crowds the break-even one, since a short line puts the two almost on top of
  // each other and the tick alone still reads.
  const drawPos = hasLine ? 50 + geometry.at(line) : 50
  const showDrawLabel = hasLine && Math.abs(drawPos - 50) >= 9

  // Who is in front rides the bar as a chip rather than sitting in its own line
  // of text, so the reading and the position are the same object. (The phone
  // drops the bar for this market and keeps only the reading — see
  // MarginReadout in LiveBetMeterMobile.jsx.)

  return (
    <div style={{ paddingTop: compact ? '13px' : '15px' }}>
      <div style={{ position: 'relative', height: TRACK_H, borderRadius: '3px', backgroundColor: TRACK_BG }}>
        <div
          className={`bc-lm-fill${bloom ? ' bc-lm-bloom' : ''}`}
          style={{
            ...fillStyle(tone),
            position: 'absolute',
            left: geometry.offset >= 0 ? '50%' : `${50 + geometry.offset}%`,
            width: `${Math.abs(geometry.offset)}%`
          }}
        />
        {/* Break-even. On a handicap this is the line; on head to head it is a
            draw, which are the same thing when the line is zero. */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '-5px',
          width: '3px',
          height: '15px',
          borderRadius: '2px',
          backgroundColor: '#94a6c0',
          transform: 'translateX(-1.5px)'
        }} />

        {/* Where the scores are actually level, which on a handicap is NOT the
            break-even point. Deliberately dimmer: it is context for the marker
            above, not the threshold the bet turns on. */}
        {hasLine && (
          <div style={{
            position: 'absolute',
            left: `${drawPos}%`,
            top: '-3px',
            width: '2px',
            height: '11px',
            borderRadius: '1px',
            backgroundColor: '#5b6d8a',
            transform: 'translateX(-1px)'
          }} />
        )}

        {/* Rides the leading edge, and actually reaches it.

            The anchor slides with the position instead of the position being
            clamped: at the far left the chip hangs off its own LEFT edge, at the
            far right off its RIGHT edge, and it passes through centred at the
            halfway mark. Since the offset it shifts by is a percentage of its own
            width, the chip is mathematically incapable of leaving the track, so
            no clamp is needed and the chip never has to stop short of the tip.

            The old version pinned the centre between 22% and 78%, which meant a
            blowout parked the chip in open track well inside the bar's end. */}
        <div
          className={`bc-lm-fill${flash ? ' bc-lm-flash' : ''}`}
          title={leader ? leadFull : undefined}
          style={{
            ...chip(tone, true),
            position: 'absolute',
            top: '50%',
            left: `${50 + geometry.offset}%`,
            transform: `translate(-${50 + geometry.offset}%, -50%)`,
            maxWidth: '48%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            zIndex: 2
          }}
        >
          {leadText}
        </div>
      </div>

      <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginTop: '9px' }}>
        {/* Both ends styled the same. These label the axis, they are not a
            verdict; colouring the backed side made it read as the winning one. */}
        <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap' }}>{opponent}</span>

        {/* Break-even, under its tick: the line on a handicap, a draw otherwise. */}
        <span style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: '#a9bbd4',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap'
        }}>
          {hasLine ? `${line > 0 ? '+' : ''}${fmt(line)}` : 'DRAW'}
        </span>

        {showDrawLabel && (
          <span style={{
            position: 'absolute',
            left: `${drawPos}%`,
            transform: 'translateX(-50%)',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#6b7f9e',
            whiteSpace: 'nowrap'
          }}>
            DRAW
          </span>
        )}

        <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap' }}>
          {backedTeam}
        </span>
      </div>
    </div>
  )
}

// ── Row ─────────────────────────────────────────────────────────────────────

/**
 * Where a game is up to, in words. Full time trumps everything; otherwise a bet
 * whose own window has closed names that moment (HT, P1 END) rather than
 * carrying on showing a period it no longer has any stake in.
 *
 * The fallback is a non-breaking space on purpose: a plain one collapses in JSX
 * and the header loses its height, shifting the period bar up.
 */
export function headingFor(live) {
  const { final, decided, windowEndLabel, periodLabel, clock } = live
  if (final) return 'FT'
  return (decided && windowEndLabel) || ([periodLabel, clock].filter(Boolean).join('  ') || ' ')
}

// Headshots and crests both come from config/playerImages.js now — one
// definition for the tracker, the EV pages, Prop EV and Pick'em. The folder is
// derived from the sport code, so a new sport needs an upload and no code.
/**
 * What image identifies a selection.
 *
 * Reads the bet's own fields rather than the live payload, so it works for a leg
 * whose game has not started and has no live data at all.
 *
 *   player prop            -> the player's face
 *   h2h / line / team total -> the backed team's crest
 *   match total             -> BOTH crests, because the market really does span
 *                              the whole game and inventing a neutral badge to
 *                              fill the slot would be decoration, not meaning
 *   anything else           -> nothing
 *
 * The kind is not always implied by which FIELD the bet arrived on. In tennis
 * the "team" IS a person, so a head to head, a handicap or an asian handicap
 * fills in team_name with a player's name. Keying the styling off the field
 * alone called those "team" and drew the face bare like a crest, with no
 * circular frame, while only aces and games-won props - the one tennis family
 * that sets player_name - came out ringed.
 *
 * A PAIR is deliberately left as a pair, even in tennis where both members are
 * faces. Two ringed circles overlapping in a 44px slot read as clutter, and a
 * cutout already carries its own silhouette. The ring marks a SINGLE subject.
 */
export function subjectFor(unit) {
  if (!unit) return null
  const sport = unit.sport

  if (unit.player_name) {
    const url = playerImageUrl(unit.player_name, sport)
    return url ? { kind: 'player', urls: [url], label: unit.player_name, sport } : null
  }

  if (unit.team_name) {
    const url = teamLogoUrl(unit.team_name, sport)
    if (!url) return null
    // One person's name in a team-shaped field, so it is a face, not a crest.
    const kind = isIndividualSport(sport) ? 'player' : 'team'
    return { kind, urls: [url], label: unit.team_name, sport }
  }

  const home = teamLogoUrl(unit.home_team, sport)
  const away = teamLogoUrl(unit.away_team, sport)
  if (home && away) {
    return {
      kind: 'pair',
      urls: [home, away],
      label: `${unit.home_team} v ${unit.away_team}`,
      sport
    }
  }

  return null
}

const fade = {
  onLoad:  e => { e.target.style.opacity = '1' },
  onError: e => { e.target.style.opacity = '0' }   // missing file, not a broken image
}

/**
 * One image. Photos get a circular frame because a square photo has to be
 * cropped to something; crests are drawn bare, since a ring around a mark that
 * already has its own silhouette just fights it.
 */
function Subject({ url, kind, sport, label }) {
  if (kind === 'player') {
    return (
      <div className="bc-lm-avatar-wrap" title={label || undefined}>
        <img
          src={url}
          alt=""
          className={`bc-lm-avatar${sport === 'NRL' ? ' bc-avatar-body' : ''}`}
          {...fade}
        />
      </div>
    )
  }

  return (
    <div className="bc-lm-logo-wrap" title={label || undefined}>
      <img src={url} alt="" className="bc-lm-logo-img" {...fade} />
    </div>
  )
}

/**
 * The image slot. Always occupies the same width so a group mixing player props
 * with team markets keeps one straight edge, and renders an empty circle when
 * there is nothing to show rather than collapsing and shifting the text.
 */
export function SubjectAvatar({ subject }) {
  // Empty slot keeps the column straight when a market has no image at all.
  if (!subject) return <div className="bc-lm-logo-wrap" />

  if (subject.kind === 'pair') {
    return (
      <div className="bc-lm-logo-pair" title={subject.label}>
        {subject.urls.map((url, i) => (
          <Subject key={i} url={url} kind="team" sport={subject.sport} label={subject.label} />
        ))}
      </div>
    )
  }

  return <Subject url={subject.urls[0]} kind={subject.kind} sport={subject.sport} label={subject.label} />
}

/**
 * What market a leg is, in the same words the tracker's Market column uses.
 *
 * A multi's row shows "Multi" in that column, so a leg is the only place this
 * can appear. Without it a head-to-head leg reads as the bare team name with no
 * way to tell it from a handicap or a team total. Handicaps get their signed
 * point appended, since "Match Handicap (Asian)" alone does not say which line
 * was taken.
 */
export function legMarketLabel(leg) {
  const market = leg?.market_type
  if (!market) return null

  const name = MARKET_TYPE_MAPPING[market]
    || String(market).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  const point = leg?.point
  if (point != null && point !== 0 && marketKindOf(leg) === 'margin') {
    return `${name} ${point > 0 ? '+' : ''}${fmt(point)}`
  }
  return name
}

/** Margin markets are the ones whose base is head_to_head or alternate_lines. */
function marketKindOf(leg) {
  const base = String(leg?.base_market_type ?? '')
  return (base === 'head_to_head' || base === 'alternate_lines') ? 'margin' : null
}

/** Groups legs by fixture, keeping first-appearance order. */
export function groupByGame(legs) {
  const order = []
  const byKey = new Map()
  for (const item of legs) {
    if (!byKey.has(item.gameKey)) {
      byKey.set(item.gameKey, [])
      order.push(item.gameKey)
    }
    byKey.get(item.gameKey).push(item)
  }
  return order.map(key => ({ key, items: byKey.get(key) }))
}

/**
 * Where a game is up to, laid out horizontally: period label, period bar, and
 * the awaiting-settlement note. Returns a fragment, so both callers drop it into
 * a flex row of their own.
 *
 * ONE header shape for a single and for a multi's fixture group. A single used
 * to stand this in a 156px column beside its meter, which is what left it no
 * room for the selection text a leg carries; running it across the top instead
 * frees the whole reserve for the same avatar-plus-text block a leg uses, so the
 * two now lay out identically from the avatar rightwards.
 */
export function GameHeader({ live }) {
  const { segments, period, completedPeriods, periodFraction, decided } = live

  return (
    <>
      <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
        {headingFor(live)}
      </span>

      <div style={{ width: '104px', flex: '0 0 auto' }}>
        <PeriodBar
          segments={segments}
          period={period}
          completedPeriods={completedPeriods}
          periodFraction={periodFraction}
        />
      </div>

      {decided && (
        // The gap between a bet being decided and being settled is up to an hour
        // (longer for a first-half bet, which waits on full time), and a row
        // sitting on pending with no explanation reads as a bug.
        <span style={{ fontSize: '10.5px', color: MUTED, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
          Awaiting settlement
        </span>
      )}
    </>
  )
}

/**
 * What the bet is: selection over market, the same two-line shape the multi
 * builders use, so a selection reads the same wherever the user meets it.
 *
 * Takes the BET (or the leg) rather than the live payload, since a leg whose
 * game has not started has no live data at all and still has to name itself.
 *
 * The market line is not decoration on either. On a multi the row above says
 * "Multi", so this is the only place a head-to-head leg can be told from a
 * handicap; on a single it is what carries the signed line.
 */
export function SelectionText({ unit, fallback, compact = false }) {
  const market = legMarketLabel(unit)

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        title={unit?.selection || undefined}
        style={{
          fontSize: compact ? '12.5px' : '12px',
          fontWeight: 700,
          color: '#f1f5f9',
          letterSpacing: '-0.01em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {unit?.selection || fallback}
      </div>

      {market && (
        <div style={{
          fontSize: '10.5px',
          color: MUTED,
          marginTop: compact ? '2px' : '1px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {market}
        </div>
      )}
    </div>
  )
}

/**
 * One expanded row's contents. `live` is whatever liveProgress() returned and
 * `bet` the row it came from; this renders nothing at all when live was null.
 */
export default function LiveBetMeter({ live, bet }) {
  if (!live) return null

  // liveProgress returns display fields rather than the raw bet, so rebuild the
  // shape subjectFor expects. Legs pass their record straight in.
  const subject = subjectFor({
    sport:       live.sport,
    player_name: live.playerName,
    team_name:   live.backedTeam,
    home_team:   live.teams?.home?.team,
    away_team:   live.teams?.away?.team
  })

  return (
    <div style={{ padding: '11px 16px 13px' }}>
      {/* Where the game is up to, across the top, exactly as a multi's fixture
          group carries it. The scoreline is deliberately NOT printed: on a
          player prop or a totals bet the team scores have no bearing on the
          selection, and reading them beside the meter invites exactly the wrong
          conclusion. Markets where the teams DO matter label their own axis
          inside the meter. Kept on hover for reference. */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '7px' }}
        title={live.scoreline || undefined}
      >
        <GameHeader live={live} />
      </div>

      {/* Centred rather than top-aligned now that the left block is an outlined
          box: against a drawn edge, a bar sitting above the box's middle reads
          as misaligned in a way it did not when the block had no edge at all. */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Everything left of the bar, in one fixed reserve shared with a multi's
            legs, so both start and end at the same x and the tracks come out the
            same length whichever kind of bet is open. */}
        <div style={{
          flex: `0 0 ${METER_INSET}`,
          width: METER_INSET,
          minWidth: 0,
          boxSizing: 'border-box',
          paddingRight: METER_GUTTER
        }}>
          <div style={SUBJECT_BOX}>
            {/* Skipped entirely when there is no image: the reserve holds the
                bar's start in place either way, so an empty circle would just be
                clutter, and the selection text takes the room instead. */}
            {subject && <SubjectAvatar subject={subject} />}

            {/* Repeated from the row above on purpose. The panel is the part
                that moves and the part being watched, so it says what it is
                about rather than sending the eye back up the row to remember. */}
            <SelectionText unit={bet} fallback={live.playerName || live.backedTeam || 'Selection'} />
          </div>
        </div>

        {/* Bet state. */}
        <div style={{ flex: 1, minWidth: 0, paddingTop: '1px' }}>
          <Meter live={live} />
        </div>
      </div>
    </div>
  )
}

// ── Multis ──────────────────────────────────────────────────────────────────

const PIP_SIZE = '15px'

/**
 * One pip per leg, in leg order, so the state of the whole multi reads in a
 * glance without parsing a sentence.
 *
 * FILLED means decided: that leg's window has closed and nothing it does from
 * here can change it, so it earns a tick or a cross. A RING means still running,
 * coloured by where it sits right now. Keeping those two apart is the point — a
 * live leg in front is not a leg won, and filling it in would claim it was.
 *
 * Tone is picked exactly as the meter picks it, off the same winningNow(), so a
 * pip can never contradict the bar it belongs to.
 */
export function LegPip({ state, winning, label }) {
  const decided = state === 'decided'
  const tone = winning == null ? TONE.none : winning ? TONE.win : (decided ? TONE.lost : TONE.live)

  // Only a won leg glows, the same rule the meters follow.
  const glow = decided && winning === true && tone.ring

  const base = {
    width: PIP_SIZE,
    height: PIP_SIZE,
    borderRadius: '50%',
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 400ms ease, border-color 400ms ease'
  }

  // Unreadable legs and legs yet to start are the same thing to the eye: nothing
  // has happened. Drawn as a bare ring with no mark inside.
  if (!decided || winning == null) {
    return (
      <div
        title={label}
        style={{
          ...base,
          backgroundColor: 'transparent',
          border: `${state === 'live' ? 2 : 1.5}px solid ${state === 'pending' ? '#35486b' : tone.main}`
        }}
      />
    )
  }

  return (
    <div
      title={label}
      style={{
        ...base,
        backgroundColor: tone.main,
        border: `1.5px solid ${tone.main}`,
        color: tone.bg,
        boxShadow: glow ? `0 0 6px ${tone.ring}, 0 0 14px ${tone.halo}` : 'none'
      }}
    >
      <svg viewBox="0 0 24 24" width="9" height="9" aria-hidden="true">
        <path
          d={winning ? 'M4 12.5 L9.5 18 L20 6.5' : 'M6.5 6.5 L17.5 17.5 M17.5 6.5 L6.5 17.5'}
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

/**
 * A multi's legs, stacked. `legs` is whatever liveLegs() returned.
 *
 * Every leg is drawn by the same GameHeader, SelectionText and Meter a single
 * uses, because a leg carries the same structured fields and is judged by the
 * same rules. The only thing this adds is the summary, and the fact that legs
 * sit in different games on different days so some are finished while others
 * have not started.
 */
export function LiveMultiMeter({ legs, open = false, onToggle }) {
  if (!legs || legs.length === 0) return null

  // A multi is lost the moment ANY leg loses, so a lost leg is the headline
  // regardless of how the rest are going.
  const lostLeg = legs.some(l => l.state === 'decided' && winningNow(l.live) === false)

  // The game to name while collapsed: the one actually in play. A multi's legs
  // sit in different games on different days, so naming any other one would be
  // pointing at a fixture nothing is happening in.
  const liveEntry = legs.find(l => l.state === 'live' && l.live) || legs.find(l => l.live)

  return (
    <div style={{ padding: '10px 16px 12px' }}>
      {/* Collapsed by default, unlike a single, which has no toggle at all. A
          single's panel is one compact row and always worth showing; a multi is
          a game header and a meter PER LEG, and five of those stacked is the one
          case that breaks "short enough that several open at once still fit".

          What survives the collapse is the pips, and they are why this is a fair
          trade: they already carry every leg's state in leg order, so the strip
          is a summary rather than a stub. */}
      <button
        type="button"
        className={`bc-lm-toggle${!open && legs.some(l => l.state === 'live') ? ' bc-lm-toggle--live' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          textAlign: 'left',
          cursor: onToggle ? 'pointer' : 'default',
          marginBottom: open ? '8px' : 0
        }}
      >
        {/* The pips replace the old "3 legs · 1 through · 2 live" sentence. They
            carry the same information in leg ORDER, which the counts could not:
            the third pip is the third leg listed below it. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {legs.map((leg, i) => (
            <LegPip
              key={i}
              state={leg.state}
              winning={winningNow(leg.live)}
              label={leg.leg?.selection || `Leg ${i + 1}`}
            />
          ))}
        </div>

        {lostLeg && (
          <span style={{ fontSize: '11px', fontWeight: 700, color: TONE.lost.main }}>
            a leg has gone down
          </span>
        )}

        {/* Where the multi is up to, while the legs themselves are hidden.
            Dropped once open, since every group below prints its own header.

            The period bar comes with it, and it is the reason no LIVE badge is
            needed: expanded, the meters filling ARE the liveness signal, but
            collapsing hides them, and a strip of static pips gives no sign the
            thing is running. A period bar creeping across its current segment
            says the same thing and says it with data. */}
        {!open && liveEntry && (
          <>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {headingFor(liveEntry.live)}
              {liveEntry.gameLabel ? `  ·  ${liveEntry.gameLabel}` : ''}
            </span>

            {liveEntry.live.segments && (
              <div style={{ width: '76px', flex: '0 0 auto' }}>
                <PeriodBar
                  segments={liveEntry.live.segments}
                  period={liveEntry.live.period}
                  completedPeriods={liveEntry.live.completedPeriods}
                  periodFraction={liveEntry.live.periodFraction}
                  marginTop={0}
                />
              </div>
            )}
          </>
        )}

        {/* A verb, not a count. The pips beside it already say how many legs
            there are and what each one is doing, so "5 legs" restated a fact and
            left the row with nothing on it that reads as an action. */}
        <span className="bc-lm-toggle-cta" style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: 'auto', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>
          {open ? 'Hide legs' : 'View legs'}
          <svg
            width="9" height="9" viewBox="0 0 12 12" aria-hidden="true"
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 200ms ease' }}
          >
            <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && groupByGame(legs).map((group, gi) => {
        // Every leg of a game shares that game's state, so the header is taken
        // from the first leg that has any. A same-game multi therefore collapses
        // to one header, and legs below it get the full width for their meters.
        const anyLive = group.items.find(item => item.live)?.live ?? null

        // Whether this fixture's legs need an image gutter at all. Now that team
        // markets have crests too this is nearly always true, but a sport with
        // no folders yet still gets a clean layout with no empty column.
        const groupHasImages = group.items.some(item => subjectFor(item.leg))

        return (
          <div key={group.key} style={{ paddingTop: gi === 0 ? '2px' : '9px', marginTop: gi === 0 ? 0 : '3px', borderTop: gi === 0 ? 'none' : '1px solid #16233c' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '7px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {group.items[0]?.gameLabel || `Game ${gi + 1}`}
              </span>

              {anyLive ? (
                <GameHeader live={anyLive} />
              ) : (
                // No fixture matched, or it has not started. Say so rather than
                // leaving a gap the eye reads as a rendering fault.
                <span style={{ fontSize: '11px', color: MUTED, whiteSpace: 'nowrap' }}>
                  {group.items[0]?.started ? 'No live data' : 'Not started'}
                </span>
              )}
            </div>

            {group.items.map((entry, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: i === group.items.length - 1 ? 0 : '16px'
                }}
              >
                {/* Same reserve a single uses, so the two lay out identically
                    from the bar rightwards. The leg's indent lives inside it. */}
                <div style={{
                  flex: `0 0 ${METER_INSET}`,
                  width: METER_INSET,
                  minWidth: 0,
                  boxSizing: 'border-box',
                  paddingLeft: '13px',
                  paddingRight: METER_GUTTER
                }}>
                  <div style={SUBJECT_BOX}>
                    {/* Reserved for the whole group, not per leg, so a group
                        mixing a player prop with a team market keeps one straight
                        edge. Groups with no props at all skip the gutter entirely
                        rather than carrying an empty column. */}
                    {groupHasImages && <SubjectAvatar subject={subjectFor(entry.leg)} />}

                    {/* Takes the rest of the reserve, so a group with no images
                        gets a wider name rather than a moved bar. */}
                    <SelectionText unit={entry.leg} fallback={`Leg ${i + 1}`} />
                  </div>
                </div>

                {/* Beside the text, not beneath it. Stacked, the track read as an
                    underline belonging to the selection; alongside, it is its own
                    object, every leg's bar lines up in one column, and the panel
                    loses a row of height per leg. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {entry.live && <Meter live={entry.live} />}
                </div>
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}
